import React, { useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from 'styled-components';

import { Header, HeaderUser, HeaderAssets, BaseLayout } from '@coinflare/ui/common';
import { GlobalStyles as UIGlobalStyle } from '@coinflare/ui/theme';

import {
    SOCKET_SUB_TYPE,
    languagesDropdownOptions,
    currenciesDropdownOptions,
    NotificationNoticeType,
    NotificationBusinessType,
} from 'src/common/constants';
import { isRtlLanguage } from 'src/common/helpers/is-rtl-language';
import { NotificationPopup } from 'src/components/features/common/notification-popup/NotificationPopup';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector, useHiddenAssets } from 'src/hooks';
import useMediaQuery from 'src/hooks/useMediaQuery';
import useToggleUserInfo from 'src/hooks/useToggleUserInfo';
import { updateThemeData } from 'src/pages/_redux';
import {
    setActiveLanguage,
    setActiveCurrency,
    setSettings,
    toggleRtl,
} from 'src/pages/_redux/global/globalsActions';
import { logout } from 'src/pages/_redux/user/usersActions';
import { removeLoggedInUserData } from 'src/pages/derivatives/_redux/derivativePageActions';
import SocketContext from 'src/setup/socket/socketContext';
import { breakpoints } from 'src/setup/theme/breakpoints';

import { Content } from './components/Content';
import { Footer } from './components/Footer';
import { GlobalStyle } from '../assets/styles';

import 'react-toastify/dist/ReactToastify.css';

interface Props {
    children: React.ReactNode;
}

const MasterLayout: React.FC<Props> = ({ children }) => {
    const { favicon: faviconIcon, announcements } = useAppSelector(
        (state) => state.globalData.indexConfig,
    );
    const { theme, commonConfig, activeLanguage, activeCurrency, notifications } = useAppSelector(
        (state) => state.globalData,
    );
    const { userInfoLoading, userinfo, userVerifyInfo, assets } = useAppSelector(
        (state) => state.userData,
    );
    const { closeSocketConnection } = useContext(SocketContext);
    const { isAuthenticated, setUnauthenticated } = useAuth();

    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery(`(max-width: calc(${breakpoints.md}))`);
    const { maskedUserId } = useToggleUserInfo(userinfo.userId);

    const favicon = document.getElementById('favicon');
    favicon && favicon.setAttribute('href', faviconIcon);
    const headerUser = useRef({});
    const headerAsset = useRef({});
    const { isAssetsHidden, toggleIsAssetsHidden } = useHiddenAssets();

    useEffect(() => {
        dispatch(updateThemeData({ isMobile: isMobile }));
    }, [dispatch, isMobile]);

    useEffect(() => {
        if (isAuthenticated) {
            headerUser.current = {
                id: maskedUserId,
                email: userinfo.email,
                kycStatus: userVerifyInfo.verifyStatus,
                isAffiliate: userinfo.isAgent,
            };
            headerAsset.current = {
                total: assets.totalAsset,
                unit: assets.unit,
                showBalance: !isAssetsHidden,
                toggleShowBalance: toggleIsAssetsHidden,
            };
        }
    }, [isAuthenticated, isAssetsHidden, assets, userinfo, userVerifyInfo, maskedUserId]);

    const changeSettings = useCallback(
        async (field: string, value: any) => {
            if (isAuthenticated) {
                const updated = { ...commonConfig, [`${field}`]: value };
                const response = await dispatch(
                    setSettings({ common_config: JSON.stringify(updated) }),
                );

                if (response?.common_config) {
                    if (field === 'lang') {
                        dispatch(setActiveLanguage(value));
                        dispatch(toggleRtl(!!isRtlLanguage(value)));
                    }

                    if (field === 'unit') {
                        dispatch(setActiveCurrency(value));
                    }
                }
            } else {
                if (field === 'lang') {
                    dispatch(setActiveLanguage(value));
                    dispatch(toggleRtl(!!isRtlLanguage(value)));
                }

                if (field === 'unit') {
                    dispatch(setActiveCurrency(value));
                }
            }
        },
        [isAuthenticated, commonConfig],
    );

    const locale = useMemo(() => {
        return {
            language: commonConfig?.lang ?? activeLanguage,
            currency: commonConfig?.unit ?? activeCurrency,
            languages: languagesDropdownOptions,
            currencies: currenciesDropdownOptions,
            changeSettings: changeSettings,
        };
    }, [commonConfig, activeLanguage, activeCurrency]);

    const notificationsData = useMemo(() => {
        if (notifications) {
            const data = notifications.userNotifications
                .filter((item) => item.noticeType !== NotificationNoticeType.POPUP)
                .map((item) => {
                    const customNotification = { ...item };
                    if (customNotification.businessType === NotificationBusinessType.PROMOTION) {
                        const customData = JSON.parse(customNotification.content);
                        customNotification.title = customData.title;
                        customNotification.content = customData.content;
                    }
                    return customNotification;
                });
            return { unread: notifications?.unread, data: data };
        }
    }, [notifications]);

    const handleLogout = () => {
        // TODO:: make common function for logout
        dispatch(logout());
        closeSocketConnection(SOCKET_SUB_TYPE.USER);
        dispatch(removeLoggedInUserData());
        setUnauthenticated();
    };

    return (
        <ThemeProvider theme={theme}>
            <UIGlobalStyle />
            <GlobalStyle />

            <NotificationPopup />

            <BaseLayout
                backgroundColor={theme.colors.background.primary}
                header={
                    <Header
                        userLoading={userInfoLoading}
                        locale={locale}
                        announcements={announcements || []}
                        notifications={notificationsData}
                        user={isAuthenticated ? (headerUser.current as HeaderUser) : undefined}
                        assets={isAuthenticated ? (headerAsset.current as HeaderAssets) : undefined}
                        logout={handleLogout}
                    />
                }
                footer={!theme.isMobile && <Footer />}
            >
                <Content color={theme.colors.text.primary}>{children}</Content>
                <ToastContainer />
            </BaseLayout>
        </ThemeProvider>
    );
};

export { MasterLayout };
